import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  MutationType,
  TagListType,
  TagListsResponseType,
} from '../../../../../shared/types';
import request from '../../../../../utils/request';
import { TAGS_PER_PAGE } from './constants';
import i18n from '../../../../../i18n';

// export const useGetTagLists = () => {
//   return useQuery('tag-lists', async () => {
//     const data: TagListsResponseType = await request('/account-tag/');
//     return data;
//   });
// };

export const useGetTagLists = ({
  callback,
  pageToken,
}: MutationType & {
  pageToken: string;
}) =>
  useQuery(
    'tag-lists',
    async () => {
      let params = '';
      params = pageToken ? `next_token=${pageToken}` : '';
      params = Array.from(new Set(params.split('&'))).join('&') || params;
      const url = `/account-tag/${
        params.length
          ? `?page_size=${TAGS_PER_PAGE}&${params}`
          : `?page_size=${TAGS_PER_PAGE}`
      }`;
      return await request(url);
    },
    {
      onSuccess: (response) => {
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
      refetchOnWindowFocus: true,
      cacheTime: 0,
    },
  );

export const useGetTagCount = ({ callback }: MutationType) => {
  return useQuery<number>(
    ['tag-count'],
    async () => {
      const data: any = await request('/account-tag/count');
      return Number(data.count || '0');
    },
    {
      onSuccess: (response: number) => {
        if (callback) callback(true, response);
      },
      onError: (err) => {
        if (callback) callback(false, err);
      },
      refetchOnWindowFocus: true,
      cacheTime: 0,
    },
  );
};

export const useSaveTagList = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (tag: TagListType) => {
      const data = await request(
        tag.id ? `/account-tag/${tag.id}` : '/account-tag/',
        tag,
        tag.id ? 'PUT' : 'POST',
      );
      return data;
    },
    {
      onSuccess: (response, tag: TagListType) => {
        queryClient.setQueryData(
          ['tag-lists', `${response.id}`],
          () => response,
        );
        if (tag.id) {
          queryClient.setQueryData('tag-lists', (old: any) => {
            const newData = { ...(old || {}) };
            if (old && old.accountTags && old.accountTags.length > 0) {
              newData.accountTags = old.accountTags.map((o: TagListType) =>
                o.id === response.id ? response : o,
              );
            }
            return newData;
          });
        } else {
          queryClient.setQueryData<TagListsResponseType>('tag-lists', (old) => {
            const newData: TagListsResponseType = { ...(old || {}) };
            if (old && old.accountTags && old.accountTags.length > 0) {
              newData.accountTags = [response, ...old.accountTags];
            } else {
              newData.accountTags = [response];
            }
            return newData;
          });
          queryClient.invalidateQueries('tag-count');
          queryClient.invalidateQueries('tag-lists');
        }

        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: tag.id
              ? `${i18n.t('forms.toast.success.tag_updated')}`
              : `${i18n.t('forms.toast.success.tag_created')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error: any, tag) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: tag.id
              ? error?.response?.data ??
                `${i18n.t('forms.toast.error.tag_updation_failed')}`
              : error?.response?.data ??
                `${i18n.t('forms.toast.error.tag_creation_failed')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useDeletetagList = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (tag: TagListType) => {
      if (!tag.id) return;
      const data = await request(`/account-tag/${tag.id}`, {}, 'DELETE');
      return data;
    },
    {
      onSuccess: (response, tag) => {
        queryClient.setQueryData<TagListsResponseType>('tag-lists', (old) => {
          const newData: TagListsResponseType = {
            ...(old || {}),
            accountTags: (old?.accountTags || []).filter(
              (t) => t.id !== tag.id,
            ),
          };
          return newData;
        });
        queryClient.invalidateQueries('tag-count');
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.tag_deleted')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.tag_deletion_failed')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};
